/**
 * @module Navbar
 * @description
 * Renders the main navigation header for the STREEN application.
 *
 * The component provides navigation to activities and stories for authenticated users, access to administrative functions for administrators, and a login entry point for unauthenticated users.
 *
 * It also provides access to the help documentation, search input, user profile menu, logout functionality, and authentication modal.
 *
 * Navigation and authentication state are managed through React Router and AuthContext. The component conditionally renders navigation items and user actions according to the current user's authentication status and role.
 *
 * @param {Object} props
 * @param {boolean} props.presentationMode
 *   Determines whether the navigation header is hidden during
 *   presentation mode.
 *
 * @requires react
 * @requires react-router-dom
 * @requires antd
 * @requires @ant-design/icons
 * @requires module:../context/authContext
 * @requires module:../Modals/AuthPanel
 * @requires module:./Notifications
 */

// #region Imports ========================================

import React, { useContext, useMemo, useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthContext from "../context/authContext";
import Logo from "../img/logo.png";
import AuthPanel from "../Modals/AuthPanel";
import { ReactComponent as Star8 } from "../css/svg/star8.svg";
import Notifications from "./Notifications";

// #endregion Imports ========================================


// #region AntDesign Imports ========================================

import { Layout, Button, message, Space, Menu, Input, Dropdown, Tooltip } from "antd";
import { LogoutOutlined, UserOutlined, EditOutlined, TeamOutlined, MenuOutlined, FundProjectionScreenOutlined, CrownOutlined, DeploymentUnitOutlined } from "@ant-design/icons";

// #endregion AntDesign Imports========================================


//#region         HELPERS & COMPONENT ALIASES ========================================
// Enable verbose logging only in development mode.
const verbose = process.env.NODE_ENV === 'development';
const vlog = (...args) => verbose && console.log(...args);

// Ant Design layout and input component aliases
const { Header, Content } = Layout;
const { Search } = Input;
//#endregion

/**
 * Renders the main STREEN navigation header.
 *
 * Provides route navigation, authentication access, user profile actions, administrative options, and access to the help documentation.
 *
 * The rendered navigation elements depend on the current user's authentication status and role. The header can be hidden when presentation mode is active.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.presentationMode - Whether presentation mode
 *   is active and the navigation header should be hidden.
 * @returns {JSX.Element} The rendered navigation header.
 */
const Navbar = ({ presentationMode }) => {
  //#region STATE & HANDLERS ==================================================

  // ROUTING
  const location = useLocation();
  const navigate = useNavigate();

  // AUTHENTICATION state of current user
  const { currentUser, logout } = useContext(AuthContext);

  // UI STATE

  // state of AuthModal visibility
  const [isModalVisible, setIsModalVisible] = useState(false);
  const showModal = () => {
    setShowTooltip(false);
    setIsModalVisible(true);
  };
  const handleCancel = () => setIsModalVisible(false);

  // state of notification
  const [showNotification, setShowNotification] = useState(false);

  // state of Searchbox
  const [searchText, setSearchText] = useState("");
  const onSearch = (value) => {
    vlog("[Navbar] Search:", value);
    setSearchText(value);
  };

  // CONTEXT & EVENT HANDLERS
  const Context = React.createContext({ name: "Default" });
  const onClick = (e) => {
    vlog("[Navbar] click ", e);
  };
  const contextValue = useMemo(
    () => ({ name: `${currentUser?.username}` }),
    []
  );

  // Defines the primary navigation tabs for authenticated users. The active tab is determined by the current route.
  const items = currentUser
    ? [

      // Only administrators can see "Verwalten".
      ...(currentUser.role === 0
        ? [

          {
            key: "aktivitaeten",
            label: (
              <Button
                className={`navbar-tab ${location.pathname === "/teacherDashboard" ? "active" : "inactive"
                  }`}
                onClick={() => navigate("/teacherDashboard")}
              >
                <span className="navbar-tab-content">
                  <DeploymentUnitOutlined className="navbar-tab-icon" />
                  <span className="navbar-tab-text">Verwalten</span>
                </span>
              </Button>
            ),
          },

        ]
        : [{
          key: "aktivitaeten",
          label: (
            <Button
              className={`navbar-tab ${location.pathname === "/activities" ? "active" : "inactive"
                }`}
              onClick={() => navigate("/activities")}
            >
              <span className="navbar-tab-content">
                <DeploymentUnitOutlined className="navbar-tab-icon" />
                <span className="navbar-tab-text">Aktivitäten</span>
              </span>
            </Button>
          ),
        },]),


      {
        key: "geschichten",
        label: (
          <Button
            className={`navbar-tab ${location.pathname === "/storiesGallery" ? "active" : "inactive"
              }`}
            onClick={() => navigate("/storiesGallery")}
          >
            <span className="navbar-tab-content">
              <CrownOutlined className="navbar-tab-icon" />
              <span className="navbar-tab-text">Geschichten</span>
            </span>
          </Button>
        ),
      },
    ] : [];

  const [showTooltip, setShowTooltip] = useState(true);

  useEffect(() => {
    vlog("[NavBar] Mounted");
    setShowTooltip(true);
  }, []);

  //#endregion


  // #region HANDLERS ==================================================

  // Logs administrative menu interactions.
  const handleMenuClick = (e) => {
    if (e.key === "logout") {
      // Handle logout logic here
      vlog("[NavBar] User clicked Menu");
    }
  };

  // Logs out the current user and displays a confirmation notification.
  const handleLogoutClick = async () => {
    const username = currentUser?.username;

    await logout();

    Notifications({
      placement: "top",
      message: `Auf Wiedersehen${username ? ` ${username}` : ""}!`,
      description: "Erfolgreich ausgeloggt!",
    });
  };

  // Navigates to the story editor.
  const handleWriteStory = () => {
    navigate("/storyEditor");
    vlog("[NavBar] Write Story button clicked");
    // Add your navigation or modal logic here
  };

  // Navigates to class management.
  const handleClasses = () => {
    navigate("/classes");
    vlog("[NavBar] Classes button clicked");
    // Add your navigation or modal logic here
  };

  // Navigates to group management.
  const handleGroups = () => {
    navigate("/groups");
    vlog("[NavBar] Groups button clicked");
    // Add your navigation or modal logic here
  };

  // Opens the digital media control panel
  const handleDMC = () => {
    navigate("/dmcPanel");
    vlog("[NavBar] dmcControlPanel button clicked");
    // Add your navigation or modal logic here
  };

  //#endregion


  // #region User Profile Menu ==================================================
  // Provides the logout action for authenticated users.
  const userProfileMenu = (
    <Menu className="user-profile-menu" onClick={handleMenuClick}>
      <Menu.Item
        key="logout"
        onClick={handleLogoutClick}
        icon={<LogoutOutlined />}
      >
        Abmeldung
        {/* Logout */}
      </Menu.Item>
    </Menu>
  );

  //#endregion


  // #region  Admin Menu ==================================================
  // Provides administrative actions, including user management, story creation, class and group management, and media controls.
  const adminMenu = (
    <Menu>
      <Menu.Item key="writeStory" icon={<EditOutlined />} onClick={handleWriteStory}>
        Geschichte schreiben {/* Write Story */}
      </Menu.Item>

      <Menu.Item key="classes" icon={<TeamOutlined />} onClick={handleClasses}>
        Klassen {/* Groups */}
      </Menu.Item>

      <Menu.Item key="groups" icon={<TeamOutlined />} onClick={handleGroups}>
        Gruppen {/* Groups */}
      </Menu.Item>

      <Menu.Item key="dmc" icon={<FundProjectionScreenOutlined />}
        onClick={handleDMC}
      >
        Mediensteuerung {/* Groups */}
      </Menu.Item>
    </Menu>
  );
  // #endregion  

  const renderUserAvatar = () => {
    if (currentUser?.username) {
      return (
        <>
          <Dropdown overlay={userProfileMenu} trigger={["click"]}>
            <div className="star-button star-button--user">
              <Star8 className="star-button__bg" />

              <span className="star-button__text">
                {currentUser.username.slice(0, 3).toUpperCase()}
              </span>
            </div>
          </Dropdown>

          {currentUser.role === 0 && (
            <Dropdown overlay={adminMenu} trigger={["click"]}>
              <Button icon={<MenuOutlined />} type="text">
                Admin Optionen
              </Button>
            </Dropdown>
          )}
        </>
      );
    }

    return (
      <Tooltip
        title="Melde dich an, um alle Funktionen zu nutzen"
        align={{ offset: [-10, -10] }}
        open={showTooltip}
      >
        <span onClick={showModal} style={{ cursor: "pointer" }}>
          <div className="star-button">
            <Star8 className="star-button__bg" />
            <UserOutlined className="star-button__icon" />
          </div>
        </span>

      </Tooltip>
    );
  };

  return (
    <Header
      className="header"
      style={{ display: presentationMode ? "none" : "block" }}
    >

      <Context.Provider value={contextValue}>
        {/* {contextHolder} */}
        <Content className="navbar">
          <Content className="container">
            <Content className="left-container">
              <Content className="logo">
                <Link to="/">
                  <img src={Logo} alt="" />
                </Link>
                {/*  <div>
                  <Typography.Text className="logo-moto">
                    LESEN. VORSTELLEN. KREIEREN.
                  </Typography.Text>
                </div> */}
              </Content>
            </Content>
            <Content className="right-container">
              <Content className="top-menu-container">
                <Menu
                  className="top-menu"
                  onClick={onClick}
                  //defaultSelectedKeys={['1']}
                  // defaultOpenKeys={['sub1']}
                  mode="horizontal"
                  items={items}
                />
              </Content>

              <Content className="help-container">
                <Space size="large">
                  <Button
                    className="navbar-help-button"
                    onClick={() => {
                      window.open(
                        process.env.REACT_APP_STREEN_DOCS_URL,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    aria-label="Hilfe"
                  >
                    ?
                  </Button>
                </Space>
              </Content>

              <Content className="search-login-container">
                <AuthPanel visible={isModalVisible} onCancel={handleCancel} />
                <Space size="large">
                  {renderUserAvatar()}
                </Space>
              </Content>
            </Content>
          </Content>
        </Content>
      </Context.Provider>
      <div className="header-topbar" />
    </Header>
  );
};

export default Navbar;
